import apiConfig from 'constants/apiConfig';
import React, { createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  icsAllQueryKeyBases,
  icsUpdateQueryKeys,
} from 'components/DisasterGPT/icsInvalidation';
import { setDchatStream } from 'slices/dchatSlice';
import { useDispatch, useStore } from 'react-redux';
import type { Store } from 'redux';
import { useSseStream, isSseDebugEnabled } from 'hooks/useSseStream';
import { useBrowserAuth } from './BrowserAuthProvider';

interface DgptStreamContextType {
  isConnected: boolean;
}

const DgptStreamContext = createContext<DgptStreamContextType | undefined>(
  undefined
);

type DgptStreamPayload = {
  type?: string;
  dchat_stream?: { dchat_id: string; user_guid: string; streamtext: string };
  dchat_ics_update?: {
    incident_id: string;
    user_guid: string;
    entity: string;
    tool: string;
  };
};

export const DgptStreamProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const store: Store = useStore();
  const { getAccessToken } = useBrowserAuth();

  function handlePayload(payload: DgptStreamPayload) {
    if (payload.type === 'connected') return;

    if (payload.dchat_stream) {
      // DGPT token stream frame. Delivered only to this user's SSE tabs by
      // coms; still filter on dchat_id so it renders only in the open chat.
      const { dchat_id, user_guid, streamtext } = payload.dchat_stream;
      const currStore = store.getState();
      const currentUserId = currStore.app.user.user_guid;
      const openDchatId = currStore.dchat.dchat?.id;
      if (
        streamtext &&
        user_guid === currentUserId &&
        dchat_id === openDchatId
      ) {
        dispatch(setDchatStream(streamtext));
      } else if (isSseDebugEnabled()) {
        const reasons: string[] = [];
        if (!streamtext) reasons.push('empty streamtext');
        if (user_guid !== currentUserId) reasons.push('user_guid mismatch');
        if (dchat_id !== openDchatId) reasons.push('dchat_id mismatch');
        console.log(
          `[dgpt-sse] [debug] dchat_stream filtered: ${reasons.join(', ')} (frame user=${user_guid} dchat=${dchat_id}, open user=${currentUserId} dchat=${openDchatId})`
        );
      }
    }

    if (payload.dchat_ics_update) {
      // The agent wrote an ICS entity (objective, action, situation
      // summary, etc.). Refetch the affected data so the open IAP/ICS
      // page reflects it immediately. Scoped to the incident the viewer
      // is on — match the ROUTED incident (source of truth for the mounted
      // workspace), Redux currentIncident, OR the open chat's incident.
      // The chat-incident check is the robust one for agent-created
      // incidents: the agent builds for the chat's incident and the user is
      // (being) navigated there, so it holds even if the route/redux lag the
      // write. invalidateQueries only refetches MOUNTED queries, so matching
      // a not-currently-viewed incident is harmless.
      const { incident_id, entity, tool } = payload.dchat_ics_update;
      const currStore = store.getState();
      const openIncidentId = currStore.app.currentIncident?.id;
      const chatIncidentId = currStore.dchat?.dchat?.incident_id;
      const routeIncidentId =
        typeof window !== 'undefined'
          ? window.location.pathname.match(/\/iap\/([0-9a-fA-F-]+)/)?.[1]
          : undefined;
      if (
        incident_id &&
        (incident_id === routeIncidentId ||
          incident_id === openIncidentId ||
          incident_id === chatIncidentId)
      ) {
        const queryBases: string[] =
          icsUpdateQueryKeys[entity] ||
          icsUpdateQueryKeys[tool] ||
          icsAllQueryKeyBases;
        queryBases.forEach((base: string) => {
          queryClient.invalidateQueries({ queryKey: [base] });
        });
      } else if (isSseDebugEnabled()) {
        console.log(
          `[dgpt-sse] [debug] dchat_ics_update skipped: incident mismatch (frame incident=${incident_id}, route=${routeIncidentId} open=${openIncidentId} chat=${chatIncidentId})`
        );
      }
    }
  }

  const { isConnected } = useSseStream<DgptStreamPayload>({
    url: apiConfig.apiGateway.dgptStream,
    onEvent: handlePayload,
    getToken: () => getAccessToken(),
    getIsTeams: () => {
      // Mirror useAxiosInterceptor's Teams detection.
      const authState = store.getState().auth;
      return (
        sessionStorage['isUsingTeams'] === 'true' ||
        authState?.teamsInitialized === true
      );
    },
    logPrefix: '[dgpt-sse]',
    maxReconnectAttempts: 4,
    reconnectDelayMs: (attempt) => attempt * 3000,
    reconnectLogLevel: 'warn',
  });

  return (
    <DgptStreamContext.Provider
      value={{
        isConnected: isConnected,
      }}
    >
      {children}
    </DgptStreamContext.Provider>
  );
};

export const useDgptStream = () => {
  const context = useContext(DgptStreamContext);
  if (!context) {
    throw new Error('useDgptStream must be used within DgptStreamProvider');
  }
  return context;
};
